<script setup lang="ts">
import dayjs from "dayjs";
import { CalendarDay, CalendarWeek } from "@/modules/base_price/utils/dayDistribution";

/**
 * The frame every month calendar in the editor is drawn in: ISO weeks as
 * rows, Monday first, the days as cells and a closing column per week. Days
 * of the neighbouring months complete the first and last week but belong to
 * — and are edited in — their own month, so they are drawn greyed and
 * carry nothing.
 *
 * What a day or a week shows is the calendar's own business, through the
 * `day` and `week` slots; `dayClass` lets it tint a day's cell by state.
 */
withDefaults(
	defineProps<{
		weeks: CalendarWeek[];
		loading?: boolean;
		/** What a greyed day's tooltip says happens to it in its own month — "distribute it there". */
		ghostHint?: string;
		dayClass?: (date: string) => string | string[] | Record<string, boolean>;
		cellMinHeight?: number;
	}>(),
	{
		loading: false,
		ghostHint: "edit it there",
		dayClass: () => "",
		cellMinHeight: 108,
	}
);

defineSlots<{
	day(props: { day: CalendarDay; week: CalendarWeek }): unknown;
	week(props: { week: CalendarWeek }): unknown;
}>();

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
</script>

<template>
	<div style="overflow-x: auto">
		<div
			class="calendar-grid"
			:class="{ 'calendar-grid--loading': loading }"
			:style="{ '--calendar-cell-min-height': `${cellMinHeight}px` }"
		>
			<div
				v-for="weekday in WEEKDAYS"
				:key="weekday"
				class="text-caption text-medium-emphasis font-weight-bold px-1"
			>
				{{ weekday }}
			</div>
			<div class="text-caption text-medium-emphasis font-weight-bold px-1">
				Week
			</div>

			<template v-for="week in weeks" :key="week.isoWeek">
				<template v-for="day in week.days" :key="day.date">
					<div v-if="day.inMonth" class="calendar-cell" :class="dayClass(day.date)">
						<slot name="day" :day="day" :week="week" />
					</div>

					<div
						v-else
						class="calendar-cell calendar-cell--ghost"
						:title="`${dayjs(day.date).format('DD MMM YYYY')} belongs to ${dayjs(day.date).format('MMMM')} — ${ghostHint}`"
					>
						<span class="text-body-2">{{ day.day }}</span>
						<span class="text-caption">{{ dayjs(day.date).format("MMM") }}</span>
					</div>
				</template>

				<div class="calendar-cell calendar-cell--week">
					<slot name="week" :week="week" />
				</div>
			</template>
		</div>
	</div>
</template>

<style scoped lang="scss">
.calendar-grid {
	display: grid;
	grid-template-columns: repeat(7, minmax(88px, 1fr)) minmax(112px, 1.2fr);
	gap: 6px;
	min-width: 760px;
}

.calendar-grid--loading {
	opacity: 0.5;
	pointer-events: none;
}

.calendar-cell {
	display: flex;
	flex-direction: column;
	min-height: var(--calendar-cell-min-height);
	padding: 6px 8px;
	border-radius: 8px;
	border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
	background: rgb(var(--v-theme-surface));
}

// Tints a calendar can give a day through `dayClass` — the colours the month
// charts use for a month in the same state.
.calendar-cell--tone-warning {
	background: rgba(var(--v-theme-warning), 0.06);
}

.calendar-cell--tone-error {
	background: rgba(var(--v-theme-error), 0.06);
}

.calendar-cell--tone-info {
	background: rgba(var(--v-theme-info), 0.06);
}

// Monthly Budget Distribution's day states — see SEASON_STATE_COLORS.
.calendar-cell--tone-locked {
	background: rgba(255, 212, 128, 0.14);
}

.calendar-cell--tone-pinned {
	background: rgba(152, 199, 78, 0.1);
}

.calendar-cell--week {
	background: rgba(var(--v-theme-on-surface), 0.04);
}

.calendar-cell--ghost {
	color: rgba(var(--v-theme-on-surface), 0.45);
	border-style: dashed;
	background: repeating-linear-gradient(
		135deg,
		rgba(var(--v-theme-on-surface), 0.02) 0 6px,
		rgba(var(--v-theme-on-surface), 0.05) 6px 12px
	);
}
</style>
